import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Stack,
  Tag,
  useColorModeValue,
  useToken,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { Select } from "chakra-react-select";
import { MeetingId } from "features/Resources/Meeting/models";
import { Text14 } from "components/Text";
import { HorizontalGreyBlackField } from "components/DataDisplay/GreyBlackField";
import ExternalLink from "components/Link";
import { useCallback, useEffect, useMemo, useState } from "react";

import { dateToAbbrevMonthYear, dateToLongStringWithTimeAndZone } from "helpers/time";
import DatePicker from "components/Form/DatePicker/BaseDatePicker";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import moment, { max } from "moment";
import {
  useMeetingDetailQuery,
  useCreateCalendarEvent,
  useEligibleForGroupQuery,
} from "features/Resources/Meeting/hooks";
import { PrimaryButton } from "components/Button";
import FindATimeView from "./FindATime";
import TimeSlotsListView from "./TimeSlotsListView";
import SchedulingWarnings from "./SchedulingWarnings";

interface InitialAdditionalMeeting {
  id: string;
  label: string;
}

interface ScheduleMeetingFormProps {
  meetingId: MeetingId;
  onComplete?: () => void;
  submitButtonText?: string;
  onSubmit: (values: { scheduleAt: string; timezone: string; additionalMeetingIds?: string[] }) => void;
  submitIsLoading?: boolean;
  initialAdditionalMeetings?: InitialAdditionalMeeting[];
  initialDate?: string;
  reservationStartTime?: string;
}

export default function ScheduleMeetingForm({
  meetingId,
  onSubmit,
  onComplete,
  submitIsLoading = false,
  submitButtonText = "Save",
  initialAdditionalMeetings,
  initialDate,
  reservationStartTime,
}: ScheduleMeetingFormProps) {
  const dateButtonColor = useColorModeValue("valueBlack.DEFAULT", "var(--chakra-colors-whiteAlpha-900)");
  // Need to use this so the value passed to timezoneselect is not "editableBackgroundGrey.light" but "#F3F3F3"
  const [lightHover, darkHover] = useToken("colors", ["editableBackgroundGrey.light", "cdsGrey.700"]);
  const borderColor = useColorModeValue("gray.200", "white");
  const dateButtonBgHover = useColorModeValue(lightHover, darkHover);
  const { data: meeting } = useMeetingDetailQuery(meetingId);
  const { data: newHireJourney } = useNewHireJourneyDetailQuery(meeting?.newHireJourneyId);
  const { mutate: createCalendarEvent, isPending: isCreatingCalendarEvent } = useCreateCalendarEvent(meetingId);
  const isGroupSessionAdhoc = meeting?.meetingType === "group_session_adhoc";
  const { data: eligibleMeetings } = useEligibleForGroupQuery(meetingId, { enabled: isGroupSessionAdhoc });
  const [additionalMeetingIds, setAdditionalMeetingIds] = useState<string[]>(() =>
    (initialAdditionalMeetings ?? []).map((m) => m.id),
  );

  const primaryNewHireName = newHireJourney?.newHire?.preferredName || newHireJourney?.newHire?.fullName || "New Hire";
  const primaryNewHireLabel = newHireJourney?.startDate
    ? `${primaryNewHireName} (starts ${dateToAbbrevMonthYear(newHireJourney.startDate)})`
    : primaryNewHireName;
  const primaryOption = useMemo(
    () => ({
      value: meetingId,
      label: primaryNewHireLabel,
      isFixed: true,
    }),
    [meetingId, primaryNewHireLabel],
  );

  const eligibleOptions = useMemo(() => {
    const fromQuery = (eligibleMeetings || []).map((m) => ({
      value: String(m.id),
      label: `${m.newHireName} (starts ${dateToAbbrevMonthYear(m.startDate)})`,
      isFixed: false,
    }));
    const queryIds = new Set(fromQuery.map((o) => o.value));
    const fromInitial = (initialAdditionalMeetings ?? [])
      .filter((m) => !queryIds.has(String(m.id)))
      .map((m) => ({ value: String(m.id), label: m.label, isFixed: false }));
    return [...fromQuery, ...fromInitial];
  }, [eligibleMeetings, initialAdditionalMeetings]);

  const selectedNewHires = useMemo(() => {
    const additional = eligibleOptions.filter((o) => additionalMeetingIds.includes(o.value));
    return [primaryOption, ...additional];
  }, [primaryOption, eligibleOptions, additionalMeetingIds]);

  const schedulingTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const [selectedDate, setSelectedDate] = useState<Date>();
  const [viewType, setViewType] = useState<"calendar" | "list">("calendar");
  const [currentChoice, setCurrentChoice] = useState<Date | undefined>(() =>
    initialDate ? new Date(initialDate) : undefined,
  );

  useEffect(() => {
    if (initialDate) {
      setSelectedDate(max(moment(initialDate), moment()).toDate());
    } else if (meeting?.scheduledAt) {
      setSelectedDate(max(moment(meeting.scheduledAt), moment()).toDate());
    } else if (meeting?.proposedAt) {
      setSelectedDate(max(moment(meeting.proposedAt), moment()).toDate());
    } else {
      setSelectedDate(moment().toDate());
    }
  }, [initialDate, meeting?.proposedAt, meeting?.scheduledAt]);

  const handleSelectTime = useCallback((time: Date) => {
    setCurrentChoice(time);
  }, []);

  const isPrevDisabled = selectedDate ? moment(selectedDate).isSameOrBefore(moment(), "day") : true;

  const handleDateChange = useCallback((date: Date) => {
    setSelectedDate(date);
    setCurrentChoice(undefined);
  }, []);

  const handlePrevDay = useCallback(() => {
    if (selectedDate && !isPrevDisabled) {
      handleDateChange(moment(selectedDate).subtract(1, "day").toDate());
    }
  }, [selectedDate, isPrevDisabled, handleDateChange]);

  const handleNextDay = useCallback(() => {
    if (selectedDate) {
      handleDateChange(moment(selectedDate).add(1, "day").toDate());
    }
  }, [selectedDate, handleDateChange]);

  if (!meetingId) {
    return <Text14>Meeting not found</Text14>;
  }

  return (
    <Stack gap="4">
      <Stack direction="row" align="center" gap="1">
        {meeting?.scheduledAt && (
          <Text14>{`Currently Scheduled: ${dateToLongStringWithTimeAndZone(meeting?.scheduledAt, schedulingTimezone)}`}</Text14>
        )}
        {meeting?.template && (
          <Text14>{`${meeting.template.durationMinutes} Minutes, ${meeting.template.proposedAtDescriptor}`}</Text14>
        )}
      </Stack>
      <Stack>
        <Box>
          <HorizontalGreyBlackField
            label={isGroupSessionAdhoc ? "New Hires" : "New Hire"}
            labelProps={{ minWidth: "120px" }}
          >
            <Box w="100%">
              {isGroupSessionAdhoc ? (
                <Select
                  isMulti
                  placeholder="Add other new hires to this session..."
                  options={eligibleOptions}
                  value={selectedNewHires}
                  onChange={(selected) => {
                    const newIds = selected.filter((s) => !s.isFixed && s.value !== meetingId).map((s) => s.value);
                    setAdditionalMeetingIds(newIds);
                  }}
                  isClearable={false}
                  size="sm"
                  chakraStyles={{
                    container: (provided) => ({ ...provided, w: "100%" }),
                    multiValueRemove: (provided, { data }) => ({
                      ...provided,
                      ...(data.isFixed ? { display: "none" } : {}),
                    }),
                  }}
                />
              ) : (
                <Tag size="sm" colorScheme="purple" borderRadius="full">
                  {primaryNewHireLabel}
                </Tag>
              )}
            </Box>
          </HorizontalGreyBlackField>
        </Box>
      </Stack>
      {meeting && <SchedulingWarnings meeting={meeting} />}

      {/* View toggle + date navigation on one row */}
      <Flex justify="space-between" align="center">
        <HStack spacing={0} borderWidth="1px" borderColor="gray.200" borderRadius="md">
          {(["calendar", "list"] as const).map((type, i) => (
            <Button
              key={type}
              size="sm"
              variant="ghost"
              px={4}
              py={2}
              borderRadius="none"
              borderLeftRadius={i === 0 ? "md" : "none"}
              borderRightRadius={i === 1 ? "md" : "none"}
              bg={viewType === type ? "cdsIndigo.50" : "transparent"}
              color={viewType === type ? "cdsIndigo.800" : "gray.600"}
              borderRightWidth={i === 0 ? "1px" : "0"}
              borderRightColor="gray.200"
              _hover={{ bg: viewType === type ? "cdsIndigo.50" : "gray.50" }}
              onClick={() => setViewType(type)}
            >
              {type === "calendar" ? "Find a Time" : "Time Slots"}
            </Button>
          ))}
        </HStack>

        <HStack spacing={1}>
          <IconButton
            aria-label="Previous day"
            icon={<ChevronLeftIcon />}
            size="sm"
            variant="ghost"
            isDisabled={isPrevDisabled}
            onClick={handlePrevDay}
          />
          <DatePicker
            minDate={moment().toDate()}
            onDateChange={handleDateChange}
            dateFormat="EEE, MMM d, yyyy"
            name="Suggested Times Date"
            value={selectedDate}
            triggerButtonProps={{
              color: dateButtonColor,
              borderWidth: "1px",
              borderColor,
              borderRadius: "md",
              height: "32px",
              fontSize: "sm",
              px: 3,
              _hover: {
                bg: dateButtonBgHover,
                borderColor: "disabledGray.DEFAULT",
              },
            }}
            includeIcon={false}
          />
          <IconButton
            aria-label="Next day"
            icon={<ChevronRightIcon />}
            size="sm"
            variant="ghost"
            onClick={handleNextDay}
          />
        </HStack>
      </Flex>
      <Divider />

      {viewType === "calendar" ? (
        <FindATimeView
          meetingId={meetingId}
          date={selectedDate}
          timezone={schedulingTimezone}
          additionalMeetingIds={additionalMeetingIds}
          selectedTime={currentChoice}
          onSelectTime={handleSelectTime}
          reservationStartTime={reservationStartTime}
        />
      ) : (
        <TimeSlotsListView
          meetingId={meetingId}
          date={selectedDate}
          timezone={schedulingTimezone}
          additionalMeetingIds={additionalMeetingIds}
          selectedTime={currentChoice}
          onSelectTime={handleSelectTime}
        />
      )}
      <Divider />
      <Flex pl="4">
        {meeting?.externalUrl ? (
          <ExternalLink to={meeting.externalUrl} text="View In Google Calendar" />
        ) : (
          <Text14>
            {"Can't find the right time? "}
            <Button
              cursor="pointer"
              variant="link"
              onClick={() =>
                createCalendarEvent(
                  {},
                  {
                    onSuccess: (res) => {
                      window.open(res.externalUrl, "_blank");
                      onComplete?.();
                    },
                  },
                )
              }
              fontSize="14px"
              isLoading={isCreatingCalendarEvent}
              loadingText="Creating Calendar Event..."
              iconSpacing="1"
              rightIcon={<ExternalLinkIcon boxSize={3} />}
            >
              Schedule in GCal
            </Button>
          </Text14>
        )}
      </Flex>
      <Flex
        w="100%"
        justify="flex-end"
        gap="2"
        position="sticky"
        bottom={0}
        bg="white"
        py={3}
        zIndex={1}
        borderTop="1px solid"
        borderColor="gray.200"
        mt={2}
        sx={{
          _after: {
            content: '""',
            position: "absolute",
            bottom: "-16px",
            left: 0,
            right: 0,
            height: "16px",
            bg: "white",
          },
        }}
      >
        <PrimaryButton
          isLoading={submitIsLoading}
          isDisabled={!currentChoice}
          onClick={() => {
            if (currentChoice) {
              onSubmit({
                scheduleAt: currentChoice.toISOString(),
                timezone: schedulingTimezone,
                additionalMeetingIds,
              });
            }
          }}
        >
          {submitButtonText}
        </PrimaryButton>
      </Flex>
    </Stack>
  );
}
